import { useState, useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import { FaFileInvoiceDollar, FaPrint } from "react-icons/fa";
import PageHeading from "../../components/PageHeading"; 
import TableSkeleton from "../../components/TableSkeleton";
import { useAuth } from "../../utils/AuthContext";
import DoctorStatementPrint from "../DoctorStatementPrint";

// API Config
const API_BASE_URL = "https://api.muktihospital.com/api";
const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const MyPaymentHistory = () => {
  const { user } = useAuth();
  const [ledgerData, setLedgerData] = useState([]);
  const [doctorInfo, setDoctorInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ totalIncome: 0 });
  const [isPrintingStatement, setIsPrintingStatement] = useState(false);

  // 1. Fetch Data Automatically on Mount
  useEffect(() => {
    const fetchMyLedger = async () => {
      console.log("Current User Object:", user);

      const doctorId = user?.doctorId || user?.id; 
      
      if (!doctorId) {
        if (user) toast.error("Doctor profile not found for this user.");
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(`${API_BASE_URL}/doctor/${doctorId}/ledger`, { 
          headers: { "x-api-key": API_KEY } 
        });
        
        setLedgerData(res.data.ledger); 
        setStats(res.data.summary);
        setDoctorInfo(res.data.doctor); 
      } catch (error) {
        console.error("Error fetching ledger:", error);
        if (error.response?.status !== 404) {
            toast.error("Failed to load payment history.");
        }
      } finally {
        setLoading(false);
      }
    };

    if (user) {
        fetchMyLedger();
    }
  }, [user]);

  // 2. Print Handler
  const handlePrintStatement = () => {
    setIsPrintingStatement(true);
    setTimeout(() => {
        window.print();
        setIsPrintingStatement(false);
    }, 100);
  };

  const breadcrumbs = [
    { label: "Dashboard", url: "/doctor-dashboard" },
    { label: "My Payments" },
  ];

  return (
    <div className={`bg-gray-50 pb-12 ${isPrintingStatement ? 'printing-statement-mode' : ''}`}>
      <ToastContainer />
      <style>{`
        #print-section { display: none; }
        @media print {
          .printing-statement-mode body * { visibility: hidden; }
          .printing-statement-mode #screen-section { display: none !important; }
          .printing-statement-mode #print-section { 
            display: block !important; 
            position: absolute; left: 0; top: 0; width: 100%; 
            background: white; visibility: visible; 
          }
          .printing-statement-mode #print-section * { visibility: visible; }
        }
      `}</style>

      {/* SCREEN VIEW */}
      <div id="screen-section">
        <PageHeading title="My Income & Commission History" breadcrumbs={breadcrumbs} />

        <div>
          
          {/* Summary Card */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
             <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 flex flex-col justify-between relative overflow-hidden h-full">
                <div className="flex items-center justify-between">
                  <div>
                      <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Total Earnings</p>
                      <h3 className="text-3xl font-bold text-green-600 mt-2">৳ {stats.totalIncome.toLocaleString()}</h3>
                  </div>
                  <div className="p-4 bg-green-50 rounded-full text-green-600">
                      <FaFileInvoiceDollar size={30}/>
                  </div>
                </div>
                <div className="mt-4 text-xs text-gray-400">
                    Lifetime earnings from NICU & Hospital Bills.
                </div>
             </div>
          </div>

          {/* Transaction Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50/50">
              <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <span className="w-2 h-6 bg-purple-600 rounded-full"></span>
                  Payment History
                </h3>
              <button 
                onClick={handlePrintStatement}
                disabled={loading || ledgerData.length === 0}
                className="flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition shadow-sm disabled:opacity-50"
              >
                <FaPrint /> Print Statement
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm text-left">
                <thead className="bg-gray-100/70 text-gray-600 uppercase font-semibold text-xs border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4">Date</th>
                    <th className="px-6 py-4">Source Bill</th>
                    <th className="px-6 py-4">Patient Name</th>
                    <th className="px-6 py-4">Type</th>
                    <th className="px-6 py-4">Note</th>
                    <th className="px-6 py-4 text-right">Credit Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr><td colSpan="6" className="p-8"><TableSkeleton rowCount={5} columns={[{width: 100},{width: 100},{width: 150},{width: 100},{width: 200},{width: 100}]} /></td></tr>
                  ) : ledgerData.length > 0 ? (
                    ledgerData.map((item, index) => (
                      <tr key={index} className="hover:bg-blue-50/30 transition duration-200">
                        <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                          {new Date(item.date).toLocaleDateString("en-GB")}
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-xs font-medium bg-gray-100 text-gray-600 px-2 py-1 rounded border border-gray-200">
                            {item.billNo}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-medium text-gray-700">
                            {item.patientName}
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                              item.type.includes('NICU') 
                              ? 'bg-purple-50 text-purple-700 border-purple-100' 
                              : 'bg-blue-50 text-blue-700 border-blue-100'
                          }`}>
                            {item.type}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-500 italic">
                            {item.note || '-'}
                        </td>
                        <td className="px-6 py-4 text-right font-bold text-green-600">
                            + ৳ {item.amount.toLocaleString()}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-16 text-center text-gray-400">
                        No transactions found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </div>

      {/* HIDDEN PRINT SECTION */}
      <div id="print-section">
        {doctorInfo && (
          <DoctorStatementPrint
            className="doctor-print-section" 
            doctor={{
                name: doctorInfo.name,
                spec: doctorInfo.specialization,
                email: doctorInfo.email
            }}
            transactions={ledgerData}
            summary={stats}
          />
        )}
      </div>

    </div>
  );
};

export default MyPaymentHistory;